import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { 
  fetchNotifications, 
  markAsRead, 
  markAllAsRead, 
  deleteNotification 
} from '../store/slices/notificationSlice';
import { X, Check, Trash2, MessageCircle, Heart, Eye, Star, Clock, CheckCheck, BellRing, Inbox, Users } from 'lucide-react';
import { Button } from './ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from './ui/badge';
import { useTheme, themes } from './ThemeProvider';

interface NotificationsPanelImprovedProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToMessages?: (chatId?: string, userId?: string) => void;
  onNavigateToActivity?: () => void;
  onViewProfile?: (profile: any) => void;
}

interface GroupedNotification {
  key: string;
  type: string;
  senderId?: string;
  senderName?: string;
  senderPhoto?: string;
  count: number;
  notifications: any[];
  latestNotification: any;
  firstNotification: any;
  isRead: boolean;
}

export const NotificationsPanelImproved = ({ 
  isOpen, 
  onClose, 
  onNavigateToMessages, 
  onNavigateToActivity,
  onViewProfile
}: NotificationsPanelImprovedProps) => {
  const dispatch = useAppDispatch();
  const { notifications, unreadCount, loading } = useAppSelector((state) => state.notification);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const hasFetched = React.useRef(false);
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    if (isOpen && !hasFetched.current) {
      dispatch(fetchNotifications({}));
      hasFetched.current = true;
    }
  }, [isOpen, dispatch]);

  // Group notifications by sender and type
  const groupNotifications = (notifs: any[]): GroupedNotification[] => {
    const groups: { [key: string]: GroupedNotification } = {};

    notifs.forEach(notification => {
      // Create a unique key for grouping: type + senderId
      const senderId = notification.metadata?.senderId || notification.metadata?.fromUserId || '';
      const key = `${notification.type}_${senderId}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          type: notification.type,
          senderId,
          senderName: notification.metadata?.senderName || notification.metadata?.fromUserName || 'Someone',
          senderPhoto: notification.metadata?.senderPhoto || notification.metadata?.fromUserPhoto,
          count: 0,
          notifications: [],
          latestNotification: notification,
          firstNotification: notification,
          isRead: true,
        };
      }

      groups[key].notifications.push(notification);
      groups[key].count++;
      
      // Update latest notification (most recent)
      if (new Date(notification.createdAt) > new Date(groups[key].latestNotification.createdAt)) {
        groups[key].latestNotification = notification;
      }

      // Update first notification (oldest)
      if (new Date(notification.createdAt) < new Date(groups[key].firstNotification.createdAt)) {
        groups[key].firstNotification = notification;
      }

      // If any notification is unread, mark group as unread
      if (!notification.isRead) {
        groups[key].isRead = false;
      }
    });

    // Convert to array and sort by latest notification date
    return Object.values(groups).sort((a, b) => 
      new Date(b.latestNotification.createdAt).getTime() - new Date(a.latestNotification.createdAt).getTime()
    );
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'interest':
        return <Heart className="w-5 h-5 text-rose-500" />;
      case 'shortlist':
        return <Star className="w-5 h-5 text-yellow-500" />;
      case 'profile_view':
        return <Eye className="w-5 h-5 text-purple-500" />;
      case 'match':
        return <Users className="w-5 h-5 text-green-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationText = (group: GroupedNotification): { title: string; message: string } => {
    const { type, senderName, count, latestNotification } = group;

    switch (type) {
      case 'message':
        if (count === 1) {
          return {
            title: `${senderName} sent you a message`,
            message: latestNotification.message || 'New message'
          };
        }
        return {
          title: `${senderName} sent you ${count} messages`,
          message: latestNotification.message || 'View messages'
        };
      
      case 'interest':
        if (count === 1) {
          return {
            title: `${senderName} sent you an interest`,
            message: latestNotification.message || 'View their profile'
          };
        }
        return {
          title: `${senderName} sent you ${count} interests`,
          message: 'View interests'
        };
      
      case 'shortlist':
        if (count === 1) {
          return {
            title: `${senderName} shortlisted you`,
            message: 'View their profile'
          };
        }
        return {
          title: `${count} people shortlisted you`,
          message: 'View shortlists'
        };
      
      case 'profile_view':
        if (count === 1) {
          return {
            title: `${senderName} viewed your profile`,
            message: 'View their profile'
          };
        }
        return {
          title: `${senderName} viewed your profile ${count} times`,
          message: 'Check who viewed you'
        };
      
      case 'match':
        return {
          title: `You matched with ${senderName}!`,
          message: 'Start chatting now'
        };
      
      default:
        return {
          title: latestNotification.title || 'Notification',
          message: latestNotification.message || 'You have a new notification'
        };
    }
  };

  const handleGroupClick = async (group: GroupedNotification) => {
    // Mark all notifications in the group as read
    const unreadNotifications = group.notifications.filter(n => !n.isRead);
    for (const notification of unreadNotifications) {
      await dispatch(markAsRead(notification._id));
    }

    onClose();

    // Navigate based on notification type
    const latestNotification = group.latestNotification;
    
    if (group.type === 'message' && onNavigateToMessages) {
      onNavigateToMessages(
        latestNotification.metadata?.chatId,
        group.senderId
      );
    } else if ((group.type === 'interest' || group.type === 'match') && onNavigateToActivity) {
      onNavigateToActivity();
    } else if (group.type === 'interest' && group.senderId && onViewProfile) {
      const profile = latestNotification.metadata?.fromUserProfile || { _id: group.senderId };
      onViewProfile(profile);
    }
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleDeleteGroup = async (e: React.MouseEvent, group: GroupedNotification) => {
    e.stopPropagation();
    // Delete all notifications in the group
    for (const notification of group.notifications) {
      await dispatch(deleteNotification(notification._id));
    }
  };

  const filteredNotifications = activeTab === 'unread' 
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const groupedNotifications = groupNotifications(filteredNotifications);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 md:hidden"
        onClick={onClose}
      />
      
      {/* Slide-in Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full md:w-[420px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.primary} p-4 text-white flex-shrink-0`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <BellRing className="w-6 h-6" />
                <h2 className="text-xl">Notifications</h2>
              </div>
              <button 
                onClick={onClose}
                className="hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 px-4 rounded-lg transition-all ${
                  activeTab === 'all' 
                    ? `bg-white ${currentTheme.primaryText} font-medium` 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 py-2 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'unread' 
                    ? `bg-white ${currentTheme.primaryText} font-medium` 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <Badge className={`${currentTheme.badge} text-white px-2 py-0.5 text-xs`}>
                    {unreadCount}
                  </Badge>
                )}
              </button>
            </div>

            {/* Mark all as read button */}
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="mt-3 w-full py-2 px-4 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading && groupedNotifications.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading notifications...</p>
                </div>
              </div>
            ) : groupedNotifications.length === 0 ? (
              <div className="flex items-center justify-center h-full px-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {activeTab === 'unread' ? (
                      <CheckCheck className="h-10 w-10 text-green-500" />
                    ) : (
                      <Inbox className="h-10 w-10 text-gray-400" />
                    )}
                  </div>
                  <h3 className="text-lg text-gray-900 mb-2">
                    {activeTab === 'unread' ? 'All Caught Up!' : 'No Notifications'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {activeTab === 'unread' 
                      ? "No unread notifications pending"
                      : "You'll see notifications here when someone interacts with you"
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {groupedNotifications.map((group) => {
                  const { title, message } = getNotificationText(group);
                  
                  return (
                    <div
                      key={group.key}
                      onClick={() => handleGroupClick(group)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !group.isRead ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 p-3 rounded-xl ${
                          group.type === 'message' ? 'bg-blue-100' :
                          group.type === 'interest' ? 'bg-rose-100' :
                          group.type === 'shortlist' ? 'bg-yellow-100' :
                          group.type === 'profile_view' ? 'bg-purple-100' :
                          group.type === 'match' ? 'bg-green-100' :
                          'bg-gray-100'
                        }`}>
                          {getNotificationIcon(group.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-gray-900 text-sm line-clamp-1">
                              {title}
                            </h4>
                            {!group.isRead && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {message}
                          </p>

                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {group.latestNotification?.createdAt 
                                ? formatDistanceToNow(new Date(group.latestNotification.createdAt), { addSuffix: true })
                                : 'Just now'
                              }
                            </p>

                            {/* Count badge */}
                            {group.count > 1 && (
                              <Badge variant="secondary" className="text-xs px-2 py-0.5">
                                {group.count} {group.type === 'message' ? 'messages' : 'notifications'}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Delete button */}
                        <button
                          onClick={(e) => handleDeleteGroup(e, group)}
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};